import type { PendingVersionSummary, FieldChangeDto } from '../api/types'

interface PendingChangeToggleProps {
  showPending: boolean
  onToggle: (showPending: boolean) => void
  pendingInfo: PendingVersionSummary
}

export function PendingChangeToggle({
  showPending,
  onToggle,
  pendingInfo,
}: PendingChangeToggleProps) {
  const formatSource = (sourceType: string, vendor?: string | null): string => {
    if (sourceType === 'vendor_email' && vendor) {
      return vendor
    }
    return sourceType === 'app_user' ? 'User' : sourceType
  }

  return (
    <div className="pending-toggle-container">
      <div className="pending-toggle-header">
        <span className="pending-toggle-source">
          Update from: <strong>{formatSource(pendingInfo.sourceType, pendingInfo.sourceVendor)}</strong>
        </span>
        <span className="pending-toggle-badge">
          {pendingInfo.fieldCount} pending change{pendingInfo.fieldCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="pending-toggle-buttons">
        <button
          className={`pending-toggle-btn ${!showPending ? 'pending-toggle-btn-active' : ''}`}
          onClick={() => onToggle(false)}
        >
          Current
        </button>
        <button
          className={`pending-toggle-btn ${showPending ? 'pending-toggle-btn-active pending-toggle-btn-pending' : ''}`}
          onClick={() => onToggle(true)}
        >
          Pending
        </button>
      </div>
    </div>
  )
}

interface PendingChangeDiffProps {
  changes: Record<string, FieldChangeDto>
  fieldLabels?: Record<string, string>
  /** Current row data from the view - used to resolve FK IDs to human-readable names */
  rowData?: Record<string, unknown>
  /** Lookup map for resolving new FK values to names (optional, for future use) */
  entityLookups?: Record<string, Record<string, string>>
}

// Mapping of FK ID columns to their human-readable view column equivalents
const FK_TO_DISPLAY_COLUMN: Record<string, string> = {
  subject_id: 'subject',
  banking_entity_id: 'banking_entity',
  performing_entity_id: 'performing_entity',
  location_performed_id: 'location_performed',
  location_destination_id: 'location_destination',
  transfer_origin_id: 'transfer_origin',
  transfer_destination_id: 'transfer_destination',
  source_sample_id: 'source_sample_label',
  blocked_by_sample_id: 'blocked_by',
}

export function PendingChangeDiff({
  changes,
  fieldLabels = {},
  rowData,
  entityLookups = {}
}: PendingChangeDiffProps) {
  const entries = Object.entries(changes)

  if (entries.length === 0) {
    return null
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  // Check if a value looks like a UUID
  const isUuid = (value: unknown): boolean => {
    if (typeof value !== 'string') return false
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  }

  // Get human-readable display value for a field
  const getDisplayValue = (fieldName: string, value: unknown, isOldValue: boolean): string => {
    const formatted = formatValue(value)

    // Check if this is a FK field that we can resolve
    const displayColumn = FK_TO_DISPLAY_COLUMN[fieldName]
    if (displayColumn && isUuid(value)) {
      // For the old value, try to get the display name from the row data
      if (isOldValue && rowData) {
        const displayValue = rowData[displayColumn]
        if (displayValue && typeof displayValue === 'string') {
          return displayValue
        }
      }
      // For new values, check if we have a lookup available
      if (!isOldValue && entityLookups[fieldName]) {
        const lookup = entityLookups[fieldName]
        const resolved = lookup[String(value)]
        if (resolved) {
          return resolved
        }
      }
      // If we can't resolve, show truncated UUID
      return `${formatted.substring(0, 8)}...`
    }

    return formatted
  }

  const getLabel = (fieldName: string): string => {
    return fieldLabels[fieldName] || fieldName.replace(/_/g, ' ')
  }

  return (
    <div className="pending-diff">
      {entries.map(([field, change]) => (
        <div key={field} className="pending-diff-row">
          <span className="pending-diff-field">{getLabel(field)}</span>
          <div className="pending-diff-values">
            <span className="pending-diff-old">{getDisplayValue(field, change.old, true)}</span>
            <span className="pending-diff-arrow">→</span>
            <span className="pending-diff-new">{getDisplayValue(field, change.new, false)}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// Helper to apply pending changes to a row for preview
export function applyPendingChanges(
  row: Record<string, unknown>,
  changes: Record<string, FieldChangeDto>
): Record<string, unknown> {
  const result = { ...row }
  for (const [field, change] of Object.entries(changes)) {
    result[field] = change.new
  }
  return result
}
